import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  FORECAST_OUTLIER_ACTIONS,
  FORECAST_OUTLIER_METHODS,
  FORECAST_OUTLIER_SCOPE_DIMENSIONS,
  FORECAST_OUTLIER_SCOPES,
  FORECAST_OUTLIER_WINDOW_MONTHS,
  isForecastOutlierThresholdValid,
} from "@erp/shared-types";

/**
 * Forecast outlier rules (`im_forecast_outlier_rules`, GEN-2301): how demand
 * spikes in sales history are found and what the forecaster sees instead. A
 * series is governed by the most specific active rule reaching it. Lifecycle is
 * revoke-and-replace, so `list` can return the edit history as well.
 */

export const ForecastOutlierScopeSchema = z.enum(FORECAST_OUTLIER_SCOPES);
export const ForecastOutlierMethodSchema = z.enum(FORECAST_OUTLIER_METHODS);
export const ForecastOutlierActionSchema = z.enum(FORECAST_OUTLIER_ACTIONS);

export const ForecastOutlierRuleRowSchema = z.object({
  id: z.string().uuid(),
  scope: ForecastOutlierScopeSchema,
  divisionId: z.string().uuid().nullable(),
  divisionName: z.string().nullable(),
  warehouseId: z.string().uuid().nullable(),
  warehouseName: z.string().nullable(),
  productV3Key: z.string().nullable(),
  method: ForecastOutlierMethodSchema,
  threshold: z.number(),
  action: ForecastOutlierActionSchema,
  /** Trailing months judged; null = the forecast run's whole history window. */
  windowMonths: z.number().int().nullable(),
  reason: z.string().nullable(),
  createdByUserId: z.string().uuid(),
  createdByUserName: z.string().nullable(),
  createdAt: z.string(),
  deactivatedAt: z.string().nullable(),
  deactivatedByUserId: z.string().uuid().nullable(),
  deactivatedByUserName: z.string().nullable(),
  supersedesRuleId: z.string().uuid().nullable(),
  isActive: z.boolean(),
});
export type ForecastOutlierRuleRow = z.infer<
  typeof ForecastOutlierRuleRowSchema
>;

/** The fields that define what a rule does, shared by create and update. */
const ruleDefinitionShape = {
  method: ForecastOutlierMethodSchema,
  threshold: z.number(),
  action: ForecastOutlierActionSchema,
  windowMonths: z
    .number()
    .int()
    .min(FORECAST_OUTLIER_WINDOW_MONTHS.min)
    .max(FORECAST_OUTLIER_WINDOW_MONTHS.max)
    .nullable(),
  reason: z.string().max(1000).nullable().optional(),
};

const THRESHOLD_MESSAGE = {
  message: "Threshold is outside the range this method accepts.",
  path: ["threshold"],
};
const thresholdInRange = (v: {
  method: (typeof FORECAST_OUTLIER_METHODS)[number];
  threshold: number;
}): boolean => isForecastOutlierThresholdValid(v.method, v.threshold);

type ScopedDimensions = {
  scope: (typeof FORECAST_OUTLIER_SCOPES)[number];
  divisionId?: string | null;
  warehouseId?: string | null;
  productV3Key?: string | null;
};

/** Mirrors the table's scope CHECK: exactly the scope's dimensions are set. */
const dimensionsMatchScope = (v: ScopedDimensions): boolean => {
  const wanted = FORECAST_OUTLIER_SCOPE_DIMENSIONS[v.scope];
  return (
    wanted.includes("division") === (v.divisionId != null) &&
    wanted.includes("warehouse") === (v.warehouseId != null) &&
    wanted.includes("product") === (v.productV3Key != null)
  );
};
const DIMENSIONS_MESSAGE = {
  message: "The division, warehouse and SKU set must match the rule's scope.",
  path: ["scope"],
};

// ─── List ────────────────────────────────────────────────────────────────────

export const ListForecastOutlierRulesInputSchema = z.object({
  /** Include revoked rows (edit history). Defaults to false. */
  includeRevoked: z.boolean().optional(),
});
export const ListForecastOutlierRulesResponseSchema = z.object({
  rules: z.array(ForecastOutlierRuleRowSchema),
});

// ─── Create ──────────────────────────────────────────────────────────────────

export const CreateForecastOutlierRuleInputSchema = z
  .object({
    scope: ForecastOutlierScopeSchema,
    divisionId: z.string().uuid().nullable().optional(),
    warehouseId: z.string().uuid().nullable().optional(),
    productV3Key: z.string().min(1).nullable().optional(),
    ...ruleDefinitionShape,
  })
  .refine(thresholdInRange, THRESHOLD_MESSAGE)
  .refine(dimensionsMatchScope, DIMENSIONS_MESSAGE);
export type CreateForecastOutlierRuleInput = z.infer<
  typeof CreateForecastOutlierRuleInputSchema
>;

/**
 * `recomputed`: an SKU x warehouse rule recomputes that one series now. Every
 * broader scope reaches too many series to recompute inline and takes effect
 * at the next forecast run.
 */
export const ForecastOutlierRuleMutationResponseSchema = z.object({
  rule: ForecastOutlierRuleRowSchema,
  recomputed: z.boolean(),
});

// ─── Update (revoke + replace) ───────────────────────────────────────────────

export const UpdateForecastOutlierRuleInputSchema = z
  .object({
    id: z.string().uuid(),
    ...ruleDefinitionShape,
  })
  .refine(thresholdInRange, THRESHOLD_MESSAGE);
export type UpdateForecastOutlierRuleInput = z.infer<
  typeof UpdateForecastOutlierRuleInputSchema
>;

// ─── Revoke ──────────────────────────────────────────────────────────────────

export const RevokeForecastOutlierRuleInputSchema = z.object({
  id: z.string().uuid(),
});

// ─── Contract ───────────────────────────────────────────────────────────────

export const forecastOutlierRulesContracts = {
  list: oc
    .input(ListForecastOutlierRulesInputSchema)
    .output(ListForecastOutlierRulesResponseSchema)
    .route({
      method: "GET",
      summary: "List forecast outlier rules",
      description:
        "Lists the organization's rules for capping or excluding demand spikes before forecasting, with scope, method, threshold and window; optionally includes revoked history.",
    }),
  create: oc
    .input(CreateForecastOutlierRuleInputSchema)
    .output(ForecastOutlierRuleMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Create a forecast outlier rule",
      description:
        "Creates a rule capping or excluding demand spikes at SKU-warehouse, SKU, warehouse, division or organization scope. SKU-warehouse rules recompute now; others apply next forecast run.",
    }),
  update: oc
    .input(UpdateForecastOutlierRuleInputSchema)
    .output(ForecastOutlierRuleMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Update a forecast outlier rule",
      description:
        "Changes an outlier rule's method, threshold, action or window by revoking it and creating a replacement at the same scope.",
    }),
  revoke: oc
    .input(RevokeForecastOutlierRuleInputSchema)
    .output(ForecastOutlierRuleMutationResponseSchema)
    .route({
      method: "POST",
      summary: "Revoke a forecast outlier rule",
      description:
        "Deactivates a forecast outlier rule so demand spikes it governed are no longer capped or excluded; reports whether the series was recomputed.",
    }),
};
